import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
	Menu, 
	MenuItem, 
	ListItemIcon, 
	ListItemText,
	Tooltip,
	Button,
	useTheme
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { trackPixelCustomEvent, trackGAEvent } from '../common/analytics';

const languages = [
	{ code: 'en', name: '🇬🇧 English' },
	{ code: 'et', name: '🇪🇪 Eesti' }
];

const LanguageSwitcher: React.FC = () => {
	const { t, i18n } = useTranslation();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const theme = useTheme();

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const changeLanguage = (lng: string) => {
		i18n.changeLanguage(lng);
		trackPixelCustomEvent('LanguageChanged', { language: lng });
		trackGAEvent({
			category: 'UI',
			action: `LanguageChanged: ${lng}`,
		});
		handleClose();
	};

	return (
		<>
			<Tooltip title={t('common.changeLanguage')}>
				<Button
					onClick={handleClick}
					aria-controls={open ? 'language-menu' : undefined}
					aria-haspopup="true"
					aria-expanded={open ? 'true' : undefined}
					startIcon={<LanguageIcon fontSize='large' />}
					sx={{
						color: theme.palette.info.main
					}}
				>
					{i18n.language}
				</Button>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				id="language-menu"
				open={open}
				onClose={handleClose}
				onClick={handleClose}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
			>
				{languages.map((lang) => (
					<MenuItem 
						key={lang.code} 
						onClick={() => changeLanguage(lang.code)}
						selected={i18n.language === lang.code}
					>
						<ListItemText>{lang.name}</ListItemText>
						{i18n.language === lang.code && (
							<ListItemIcon>
								<CheckIcon fontSize="small" />
							</ListItemIcon>
						)}
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

export default LanguageSwitcher; 